import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../lib/auth';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';

import {
  partnerCategories,
  partnerTypes,
  kycDocumentTypes,
  partnerTypeLabels,
  addressTypeLabels,
  getDocumentTypeLabel,
  isAgreementCopyDocument,
  categoriesForPartnerType,
  stripWebsiteProtocol,
  withHttps,
  constitutionTypeLabels,
  validateDocumentFileExtension
} from '../../lib/utils';
import {
  normalizeLinkedMobileInput,
  normalizeUpiIdInput,
  sanitizeAccountNumberInput,
  sanitizeLinkedMobileKeystroke,
  sanitizeUpiIdKeystroke,
  validateAccountNumber,
  validateBranchName,
  validateIfscCode,
  validateLinkedMobile,
  validateUpiId,
} from '../../lib/bankingValidation';

import LocationFields from '../../components/LocationFields';
import { ContactVerificationControls } from '../../components/ContactVerification';
import KycDocumentUploadStep from '../../components/KycDocumentUploadStep';

import {
  User,
  Briefcase,
  MapPin,
  Landmark,
  FileText,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  X,
  Loader2,
  FileSignature,
} from 'lucide-react';
import DsaEmpanelmentAgreementContent, {
  DSA_EMPANELMENT_AGREEMENT_VERSION,
  DSA_EMPANELMENT_AGREEMENT_TYPE,
} from '../../components/DsaEmpanelmentAgreementContent';
import {
  AgreementPdfEmbed,
  AgreementDocumentMeta,
  REGISTRATION_AGREEMENT_SHOW_DOWNLOAD,
} from '../../components/AgreementPdfEmbed';
import AgreementDocumentPanel from '../../components/agreements/AgreementDocumentPanel';
import { AGREEMENT_KEYS } from '../../lib/agreementDocuments';

const steps = [
  { id: 'basic', label: 'Basic Info', icon: User },
  { id: 'business', label: 'Business', icon: Briefcase },
  { id: 'address', label: 'Address', icon: MapPin },
  { id: 'banking', label: 'Payment', icon: Landmark },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'agreement', label: 'Confidentiality Agreement', shortLabel: 'Confidentiality', icon: FileSignature },
  { id: 'conduct', label: 'AML Policy', icon: FileSignature },
  { id: 'dsa_empanelment', label: 'Agreement', icon: FileSignature },
  { id: 'review', label: 'Review', icon: CheckCircle2 },
];

const LAST_STEP_INDEX = steps.length - 1;
const DSA_STEP_INDEX = 7;
const REVIEW_STEP_INDEX = 8;

function AddressStep({ addresses, setAddresses }) {
  const updateField = (idx, field, value) => {
    const updated = [...addresses];
    updated[idx][field] = value;
    setAddresses(updated);
  };

  // India/GSTN address rules:
  // - Allowed chars: alphanumeric, space, - / & , ' # . ( ) " : ; _
  // - Must NOT start with a special character (first char must be alphanumeric)
  const ADDRESS_LINE_ALL_CHARS = /^[a-zA-Z0-9\s\-/&,'#.()":;_]*$/; // used while typing (before first char locks in)
  const updateAddressLine = (idx, field, value) => {
    if (value === '') { updateField(idx, field, value); return; }
    // Block disallowed chars at any position
    if (!ADDRESS_LINE_ALL_CHARS.test(value)) return;
    // Block special char as first character
    if (!/^[a-zA-Z0-9]/.test(value)) return;
    updateField(idx, field, value);
  };

  const updateLocation = (idx, loc) => {
    setAddresses((prev) => {
      const updated = [...prev];
      updated[idx] = {
        ...updated[idx],
        state: loc.state || '',
        city: loc.city || '',
        pincode: loc.pincode || '',
        state_id: loc.state_id || '',
        city_id: loc.city_id || '',
      };
      return updated;
    });
  };

  const removeAddress = (idx) => {
    setAddresses(prev => prev.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-semibold text-slate-900">Address Details</h2>
          <p className="text-sm text-slate-500 mt-1">Add your registered and/or office address</p>
        </div>
        <Button variant="outline" size="sm" className="rounded-xl text-xs border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-700" onClick={() => setAddresses(prev => [...prev, { address_type: 'office', address_line1: '', address_line2: '', city: '', state: '', pincode: '', country: 'India', state_id: '', city_id: '' }])}>
          + Add Address
        </Button>
      </div>
      {addresses.map((addr, idx) => (
        <div key={addr._id || idx} className="border border-slate-200 rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <Select value={addr.address_type} onValueChange={v => updateField(idx, 'address_type', v)}>
              <SelectTrigger className="w-44 rounded-xl border-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['registered', 'office', 'correspondence'].map(t => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}
              </SelectContent>
            </Select>
            {addresses.length > 1 && (
              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg" onClick={() => removeAddress(idx)}><X className="w-4 h-4" /></Button>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Input value={addr.address_line1} onChange={e => updateAddressLine(idx, 'address_line1', e.target.value)} placeholder="Address Line 1 *" className="rounded-xl border-slate-200" />
            </div>
            <div className="sm:col-span-2">
              <Input value={addr.address_line2} onChange={e => updateAddressLine(idx, 'address_line2', e.target.value)} placeholder="Address Line 2" className="rounded-xl border-slate-200" />
            </div>
            <LocationFields
              value={{
                state: addr.state,
                city: addr.city,
                pincode: addr.pincode,
                state_id: addr.state_id,
                city_id: addr.city_id,
              }}
              onChange={(loc) => updateLocation(idx, loc)}
              showPincode
              showCountry
              country={addr.country || 'India'}
              variant="wizard"
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function AgreementStep({ agreementAccepted, setAgreementAccepted }) {
  return (
    <div className="space-y-4">
      <AgreementDocumentPanel
        agreementKey={AGREEMENT_KEYS.CONFIDENTIALITY}
        title="Partner Confidentiality & Data Protection Undertaking"
        subtitle="This undertaking protects DhanSthal's business information and the personal data of customers you refer — not your own personal data, which is covered separately under the Partner Policy Agreement."
        showDownload
      />

      <div className="bg-blue-50/80 border border-blue-200/80 rounded-2xl p-5">
        <div className="flex items-start gap-3">
          <Checkbox
            id="agreement-accept"
            checked={agreementAccepted}
            onCheckedChange={(checked) => setAgreementAccepted(checked === true)}
            className="mt-0.5"
            data-testid="agreement-accept-checkbox"
          />
          <label htmlFor="agreement-accept" className="text-sm font-medium cursor-pointer leading-relaxed text-blue-800">
            I understand this is a one-way undertaking. I agree not to disclose DhanSthal's business information or misuse customer data I access while referring business.
          </label>
        </div>
      </div>
    </div>
  );
}

function ConductStep({ conductAccepted, setConductAccepted }) {
  return (
    <div className="space-y-4">
      <AgreementDocumentPanel
        agreementKey={AGREEMENT_KEYS.AML_POLICY}
        title="Anti-Money Laundering (AML) Policy Agreement"
        subtitle="Read the AML policy below and accept to proceed"
        showDownload
      />

      <div className="bg-blue-50/80 border border-blue-200/80 rounded-2xl p-5">
        <div className="flex items-start gap-3">
          <Checkbox
            id="conduct-accept"
            checked={conductAccepted}
            onCheckedChange={(checked) => setConductAccepted(checked === true)}
            className="mt-0.5"
            data-testid="conduct-accept-checkbox"
          />
          <label htmlFor="conduct-accept" className="text-sm font-medium cursor-pointer leading-relaxed text-blue-800">
            I accept the Anti-Money Laundering (AML) Policy Agreement and confirm my compliance with all AML/KYC obligations.
          </label>
        </div>
      </div>
    </div>
  );
}

AgreementStep.propTypes = {
  agreementAccepted: PropTypes.bool.isRequired,
  setAgreementAccepted: PropTypes.func.isRequired,
};

ConductStep.propTypes = {
  conductAccepted: PropTypes.bool.isRequired,
  setConductAccepted: PropTypes.func.isRequired,
};

function DsaEmpanelmentStep({
  partnerId,
  dsaDigitalAccepted,
  setDsaDigitalAccepted,
  agreementCopyDoc,
  setAgreementCopyDoc,
}) {
  const [generating, setGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [generateError, setGenerateError] = useState('');

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;

    const loadPopulatedAgreement = async () => {
      if (!partnerId) return;
      setGenerating(true);
      setGenerateError('');
      try {
        const genRes = await api.post(`/api/partners/${partnerId}/agreement/generate-dsa`);
        const doc = genRes.data?.document;
        if (!doc?.id) throw new Error('Agreement document missing');
        if (!cancelled) setAgreementCopyDoc(doc);

        const urlRes = await api.get(`/api/documents/${doc.id}/url`);
        let url = urlRes.data?.url;
        if (urlRes.data?.isProxy) {
          const blobRes = await api.get(`/api/documents/${doc.id}/content`, { responseType: 'blob' });
          objectUrl = URL.createObjectURL(blobRes.data);
          url = objectUrl;
        }
        if (!cancelled) setPreviewUrl(url || '');
      } catch (err) {
        if (!cancelled) {
          const detail = err?.response?.data?.detail;
          setGenerateError(typeof detail === 'string' ? detail : 'Failed to generate Agreement PDF');
        }
      } finally {
        if (!cancelled) setGenerating(false);
      }
    };

    loadPopulatedAgreement();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [partnerId, setAgreementCopyDoc]);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-lg font-semibold text-slate-900">Agreement</h2>
        <p className="text-sm text-slate-500 mt-1">
          Review the Dhansthal Business Associate / DSA Empanelment Agreement populated with your registration details, then accept to continue.
        </p>
      </div>

      <div className="rounded-2xl border border-slate-200 overflow-hidden bg-white shadow-sm">
        {generating && (
          <div className="h-[420px] flex flex-col items-center justify-center gap-3 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin text-[#0066CC]" />
            <p className="text-sm">Generating your populated Agreement PDF…</p>
          </div>
        )}
        {!generating && generateError && (
          <div className="h-[220px] flex flex-col items-center justify-center gap-2 px-6 text-center">
            <p className="text-sm text-red-600">{generateError}</p>
            <p className="text-xs text-slate-500">Complete earlier registration steps and try again.</p>
          </div>
        )}
        {!generating && !generateError && previewUrl && (
          <AgreementPdfEmbed
            previewUrl={previewUrl}
            title="Populated DSA Empanelment Agreement"
            testId="dsa-agreement-pdf-preview"
          />
        )}
      </div>

      {agreementCopyDoc && (
        <AgreementDocumentMeta
          documentName={agreementCopyDoc.document_name}
          version={DSA_EMPANELMENT_AGREEMENT_VERSION}
          previewUrl={previewUrl}
          showDownload={REGISTRATION_AGREEMENT_SHOW_DOWNLOAD}
        />
      )}

      <div className="bg-blue-50/80 rounded-2xl p-5 border border-blue-100">
        <div className="flex items-start gap-3">
          <Checkbox
            id="dsa-agreement-accept"
            checked={dsaDigitalAccepted}
            disabled={generating || !!generateError || !agreementCopyDoc}
            onCheckedChange={(checked) => setDsaDigitalAccepted(checked === true)}
            data-testid="dsa-agreement-checkbox"
            className="mt-0.5"
          />
          <label
            htmlFor="dsa-agreement-accept"
            className={`text-sm font-medium leading-relaxed text-blue-800 ${generating || generateError || !agreementCopyDoc ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
          >
            I agree to the above Agreement
            <span className="block text-[11px] font-normal text-blue-700/80 mt-1">
              I have reviewed the populated Dhansthal Business Associate / DSA Empanelment Agreement (Version {DSA_EMPANELMENT_AGREEMENT_VERSION}) and agree to its terms and conditions.
            </span>
          </label>
        </div>
      </div>
    </div>
  );
}

DsaEmpanelmentStep.propTypes = {
  partnerId: PropTypes.string,
  dsaDigitalAccepted: PropTypes.bool.isRequired,
  setDsaDigitalAccepted: PropTypes.func.isRequired,
  agreementCopyDoc: PropTypes.object,
  setAgreementCopyDoc: PropTypes.func.isRequired,
};

AddressStep.propTypes = {
  addresses: PropTypes.array.isRequired,
  setAddresses: PropTypes.func.isRequired,
};

function WizardStepper({ currentStep }) {
  return (
    <Card className="rounded-2xl border border-slate-200/80 bg-white shadow-[0_10px_30px_rgba(2,8,23,0.08)]" data-testid="partner-registration-stepper">
      <CardContent className="px-4 sm:px-6 py-4">
        {/* Mobile: compact progress indicator */}
        <div className="flex items-center justify-between sm:hidden mb-1">
          <span className="text-xs font-semibold text-[#0066CC]">Step {currentStep + 1} of {steps.length}</span>
          <span className="text-xs font-medium text-slate-600">{steps[currentStep].label}</span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-1.5 sm:hidden">
          <div className="bg-emerald-500 h-1.5 rounded-full transition-all duration-300" style={{ width: `${((currentStep) / (steps.length - 1)) * 100}%` }} />
        </div>

        {/* Desktop: horizontal stepper */}
        <div className="hidden sm:flex items-center justify-between">
          {steps.map((step, i) => {
            const isCompleted = i < currentStep;
            const isActive = i === currentStep;
            const StepIcon = step.icon;
            let stepCircleClass = 'bg-white border-2 border-[#e2e8f0] text-[#94a3b8]';
            if (isCompleted) stepCircleClass = 'bg-[#0066CC] text-white shadow-[0_2px_8px_rgba(0,102,204,0.3)]';
            else if (isActive) stepCircleClass = 'bg-[#0066CC] text-white shadow-[0_0_0_4px_rgba(0,102,204,0.12)]';
            return (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center gap-1.5 flex-shrink-0 relative z-10">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${stepCircleClass}`}>
                    {isCompleted ? <CheckCircle2 className="w-4.5 h-4.5" /> : <StepIcon className="w-4 h-4" />}
                  </div>
                  <span className={`text-[11px] font-medium whitespace-nowrap ${
                    isActive || isCompleted ? 'text-[#0066CC]' : 'text-[#94a3b8]'
                  }`} title={step.label}>
                    {step.shortLabel || step.label}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div className={`flex-1 h-[2.5px] mx-1 rounded-full transition-all duration-300 self-start mt-5 ${
                    isCompleted ? 'bg-[#0066CC]' : 'bg-[#e2e8f0]'
                  }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

WizardStepper.propTypes = {
  currentStep: PropTypes.number.isRequired,
};

function WizardNavigation({
  currentStep,
  saving,
  submitting,
  termsAccepted,
  agreementAccepted,
  conductAccepted,
  dsaStepSatisfied,
  onBack,
  onNext,
  onSubmit,
}) {
  const backButton = currentStep > 0 ? (
    <button
      onClick={onBack}
      className="inline-flex items-center gap-1.5 h-10 px-4 text-[13px] font-medium text-[#334155] bg-white border border-[#E2E8F0] rounded-xl hover:bg-[#f8fafc] hover:border-[#93c5fd] transition-colors"
    >
      <ArrowLeft className="w-4 h-4" /> Back
    </button>
  ) : <div />;

  const nextDisabled = saving
    || (currentStep === 5 && !agreementAccepted)
    || (currentStep === 6 && !conductAccepted)
    || (currentStep === DSA_STEP_INDEX && !dsaStepSatisfied);

  const nextButton = currentStep < LAST_STEP_INDEX ? (
    <Button
      onClick={onNext}
      disabled={nextDisabled}
      className="rounded-xl bg-[#0066CC] text-white hover:bg-[#005bb5] shadow-sm focus-visible:ring-2 focus-visible:ring-[#0066CC] focus-visible:ring-offset-2"
      data-testid="wizard-next-btn"
    >
      {saving ? (
        <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving...</>
      ) : (
        <>Save & Continue <ArrowRight className="w-4 h-4 ml-1.5" /></>
      )}
    </Button>
  ) : (
    <Button
      onClick={onSubmit}
      disabled={submitting || !termsAccepted}
      className="rounded-xl bg-[#0066CC] text-white hover:bg-[#005bb5] shadow-sm focus-visible:ring-2 focus-visible:ring-[#0066CC] focus-visible:ring-offset-2"
      data-testid="wizard-submit-btn"
    >
      {submitting ? (
        <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Submitting...</>
      ) : (
        <>Submit Application <CheckCircle2 className="w-4 h-4 ml-1.5" /></>
      )}
    </Button>
  );

  return (
    <div className="flex items-center justify-between pt-4">
      {backButton}
      {nextButton}
    </div>
  );
}

WizardNavigation.propTypes = {
  currentStep: PropTypes.number.isRequired,
  saving: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  termsAccepted: PropTypes.bool.isRequired,
  agreementAccepted: PropTypes.bool.isRequired,
  conductAccepted: PropTypes.bool.isRequired,
  dsaStepSatisfied: PropTypes.bool.isRequired,
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

function AgreementPreviewModal({ onClose, mode }) {
  const isDsa = mode === 'dsa';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="relative bg-white rounded-2xl shadow-2xl w-[90vw] max-w-4xl h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="font-display font-semibold text-lg text-slate-900">
            {isDsa ? 'DSA Empanelment Agreement' : 'Partner Agreement'}
          </h3>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-hidden p-4">
          {isDsa ? (
            <div className="w-full h-full overflow-y-auto rounded-lg border border-slate-200 bg-white">
              <DsaEmpanelmentAgreementContent />
            </div>
          ) : (
            <iframe
              src="/partner-agreement.pdf"
              className="w-full h-full rounded-lg border border-slate-200"
              title="Partner Agreement Preview"
            />
          )}
        </div>
      </div>
    </div>
  );
}

AgreementPreviewModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['nda', 'dsa']),
};

export default function RegistrationWizard() {
  const { user, refreshUser, updateUser } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [partnerId, setPartnerId] = useState(user?.partner_id || null);
  const [loading, setLoading] = useState(!!user?.partner_id);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [verificationGateMessage, setVerificationGateMessage] = useState('');
  const [emailVerified, setEmailVerified] = useState(!!user?.is_email_verified);
  const [mobileVerified, setMobileVerified] = useState(!!user?.is_mobile_verified);

  // Form states
  const [basic, setBasic] = useState({
    partner_type: 'individual', category: '', category_other: '', display_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
    email: user?.email || '', mobile: user?.mobile || '', first_name: user?.first_name || '', last_name: user?.last_name || ''
  });
  const [business, setBusiness] = useState({
    constitution_type: '', business_name: '', business_description: '', years_in_business: '',
    pan_number: '', gst_number: '', website_url: '', secondary_email: '', secondary_mobile: '', designation: ''
  });
  const [addresses, setAddresses] = useState([
    { address_type: 'registered', address_line1: '', address_line2: '', city: '', state: '', pincode: '', country: 'India', state_id: '', city_id: '' }
  ]);
  const [banking, setBanking] = useState({
    upi_id: '', linked_mobile: '',
    account_holder_name: '', bank_name: '', branch_name: '', account_number: '', ifsc_code: '', account_type: 'savings'
  });
  const [documents, setDocuments] = useState([]);
  const [agreementCopyDoc, setAgreementCopyDoc] = useState(null);
  const [agreementCopyError, setAgreementCopyError] = useState('');
  const [uploadingDocType, setUploadingDocType] = useState(null);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [conductAccepted, setConductAccepted] = useState(false);
  const [dsaDigitalAccepted, setDsaDigitalAccepted] = useState(false);
  const [previewAgreementMode, setPreviewAgreementMode] = useState(null);

  // Validation error states
  const [businessErrors, setBusinessErrors] = useState({});
  const [bankingErrors, setBankingErrors] = useState({});

  // ===== VALIDATION CONSTANTS =====
  const CONSTITUTION_TYPES = ['proprietorship', 'partnership', 'pvt_ltd', 'llp', 'individual'];

  // ===== BUSINESS DETAILS VALIDATION =====
  const validateBusinessName = (value) => {
    if (!value?.trim()) return 'Business name is required';
    const trimmed = value.trim();
    if (trimmed.length < 3) return 'Minimum 3 characters required';
    if (trimmed.length > 100) return 'Maximum 100 characters allowed';
    if (/^[^a-zA-Z0-9]/.test(trimmed)) return 'Cannot start with a special character';
    if (!/^[a-zA-Z0-9][a-zA-Z0-9\s&.-]*$/.test(trimmed)) return 'Only alphabets, numbers, spaces, &, ., - allowed';
    return '';
  };

  const validateYearsInBusiness = (value) => {
    if (value === '' || value === null || value === undefined) return 'Years in business is required';
    const strVal = String(value);
    if (!/^\d+$/.test(strVal)) return 'Only positive whole numbers allowed';
    const num = parseInt(strVal, 10);
    if (num < 0) return 'Negative values not allowed';
    if (num > 200) return 'Please enter a valid number';
    return '';
  };

  const validateDesignation = (value) => {
    if (!value?.trim()) return 'Designation is required';
    const trimmed = value.trim();
    if (trimmed.length < 2) return 'Minimum 2 characters required';
    if (trimmed.length > 100) return 'Maximum 100 characters allowed';
    if (!/^[a-zA-Z\s]+$/.test(trimmed)) return 'Only alphabets and spaces allowed';
    return '';
  };

  const validateBusinessDescription = (value) => {
    if (!value?.trim()) return 'Business description is required';
    if (value.length > 500) return 'Maximum 500 characters allowed';
    if (/<script[\s>]|<\/script>/i.test(value)) return 'Invalid characters detected';
    if (/[<>{}]/.test(value)) return 'Special characters <, >, {, } are not allowed';
    return '';
  };

  const validateWebsite = (value) => {
    if (!value?.trim()) return '';
    try {
      const url = new URL(withHttps(value));
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return 'Only HTTP/HTTPS URLs are allowed';
      }
      if (!url.hostname || !url.hostname.includes('.')) {
        return 'Enter a valid website like www.example.com';
      }
      return '';
    } catch {
      return 'Invalid website URL';
    }
  };

  const validatePanNumber = (value) => {
    if (!value?.trim()) return 'PAN number is required';
    const trimmed = value.trim().toUpperCase();
    if (!/^[A-Z]{5}\d{4}[A-Z]$/.test(trimmed)) return 'Invalid PAN format (e.g., ABCDE1234F)';
    return '';
  };

  const validateGstNumber = (value) => {
    if (!value?.trim()) return ''; // GST is optional
    const trimmed = value.trim().toUpperCase();
    if (!/^\d{2}[A-Z]{5}\d{4}[A-Z][\dA-Z]Z[\dA-Z]$/.test(trimmed)) return 'Invalid GST format (e.g., 22ABCDE1234F1Z5)';
    return '';
  };

  const validateAllBusiness = () => {
    const errors = {};
    errors.business_name = validateBusinessName(business.business_name);
    errors.pan_number = validatePanNumber(business.pan_number);
    errors.gst_number = validateGstNumber(business.gst_number);
    errors.years_in_business = validateYearsInBusiness(business.years_in_business);
    errors.designation = validateDesignation(business.designation);
    errors.business_description = validateBusinessDescription(business.business_description);
    errors.website_url = validateWebsite(business.website_url);
    setBusinessErrors(errors);
    return !Object.values(errors).some(e => e);
  };

  // ===== BANKING DETAILS VALIDATION =====
  const validateAccountHolderName = (value) => {
    if (!value?.trim()) return 'Account holder name is required';
    const trimmed = value.trim();
    if (trimmed.length < 2) return 'Minimum 2 characters required';
    if (trimmed.length > 200) return 'Maximum 200 characters allowed';
    if (!/^[a-zA-Z\s.]+$/.test(trimmed)) return 'Only alphabets, spaces, and periods allowed';
    return '';
  };

  const validateBankName = (value) => {
    if (!value?.trim()) return 'Bank name is required';
    const trimmed = value.trim();
    if (trimmed.length < 2) return 'Minimum 2 characters required';
    if (trimmed.length > 200) return 'Maximum 200 characters allowed';
    if (!/^[a-zA-Z0-9][a-zA-Z0-9\s&.\-()]*$/.test(trimmed)) return 'Invalid bank name format';
    return '';
  };

  const validateAllBanking = () => {
    const errors = {
      upi_id: validateUpiId(banking.upi_id),
      linked_mobile: validateLinkedMobile(banking.linked_mobile),
      account_holder_name: validateAccountHolderName(banking.account_holder_name),
      bank_name: validateBankName(banking.bank_name),
      account_number: validateAccountNumber(banking.account_number),
      ifsc_code: validateIfscCode(banking.ifsc_code),
    };
    setBankingErrors(errors);
    return !Object.values(errors).some(e => e);
  };

  const maskAccountNumber = (value) => {
    if (!value || value.length < 4) return value || '-';
    return `****${value.slice(-4)}`;
  };

  // ===== INPUT SANITIZATION HANDLERS =====
  const handleBusinessNameChange = (e) => {
    let value = e.target.value;
    // Allow only alphabets, numbers, spaces, &, ., -
    value = value.replace(/[^a-zA-Z0-9\s&.-]/g, '');
    if (value.length > 100) value = value.slice(0, 100);
    setBusiness(p => ({ ...p, business_name: value }));
    if (businessErrors.business_name) {
      setBusinessErrors(prev => ({ ...prev, business_name: validateBusinessName(value) }));
    }
  };

  const handleYearsChange = (e) => {
    let value = e.target.value;
    // Strip non-digit characters including minus and decimal
    value = value.replace(/\D/g, '');
    if (value.length > 3) value = value.slice(0, 3);
    setBusiness(p => ({ ...p, years_in_business: value }));
    if (businessErrors.years_in_business) {
      setBusinessErrors(prev => ({ ...prev, years_in_business: validateYearsInBusiness(value) }));
    }
  };

  const handleDesignationChange = (e) => {
    let value = e.target.value;
    // Allow only alphabets and spaces
    value = value.replace(/[^a-zA-Z\s]/g, '');
    if (value.length > 100) value = value.slice(0, 100);
    setBusiness(p => ({ ...p, designation: value }));
    if (businessErrors.designation) {
      setBusinessErrors(prev => ({ ...prev, designation: validateDesignation(value) }));
    }
  };

  const handleDescriptionChange = (e) => {
    let value = e.target.value;
    if (value.length > 500) value = value.slice(0, 500);
    // Remove dangerous characters (neutralizes script tags without regex backtracking risk)
    value = value.replace(/[<>{}]/g, '');
    setBusiness(p => ({ ...p, business_description: value }));
    if (businessErrors.business_description) {
      setBusinessErrors(prev => ({ ...prev, business_description: validateBusinessDescription(value) }));
    }
  };

  const handleWebsiteChange = (e) => {
    const value = stripWebsiteProtocol(e.target.value);
    setBusiness(p => ({ ...p, website_url: value }));
    if (businessErrors.website_url) {
      setBusinessErrors(prev => ({ ...prev, website_url: validateWebsite(value) }));
    }
  };

  const handlePANChange = (e) => {
    let v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
    const patterns = [/[A-Z]/, /[A-Z]/, /[A-Z]/, /[A-Z]/, /[A-Z]/, /\d/, /\d/, /\d/, /\d/, /[A-Z]/];
    let sanitized = '';
    for (let i = 0; i < v.length; i++) {
      if (patterns[i].test(v[i])) sanitized += v[i]; else break;
    }
    setBusiness(p => ({ ...p, pan_number: sanitized }));
    if (businessErrors.pan_number) {
      setBusinessErrors(prev => ({ ...prev, pan_number: '' }));
    }
  };

  const handleGSTChange = (e) => {
    setBusiness(p => ({ ...p, gst_number: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }));
    if (businessErrors.gst_number) {
      setBusinessErrors(prev => ({ ...prev, gst_number: '' }));
    }
  };

  const mergeFields = (prev, source, transform) => {
    const updated = { ...prev };
    Object.keys(prev).forEach(k => { if (source[k]) updated[k] = transform ? transform(source[k]) : source[k]; });
    return updated;
  };

  // Load existing data if partner exists
  const loadPartnerData = (d) => {
    // Redirect if partner already submitted (not in draft/rejected)
    if (d.partner?.status && !['draft', 'rejected'].includes(d.partner.status)) {
      navigate('/partner');
      return;
    }
    applyPartnerFormData(d);
  };

  const applyPartnerFormData = (d) => {
    if (d.partner) {
      setBasic(prev => ({
        ...prev,
        partner_type: d.partner.partner_type || prev.partner_type,
        category: d.partner.category || prev.category,
        category_other: d.partner.category_other || '',
        display_name: d.partner.display_name || prev.display_name
      }));
    }
    if (d.profile) {
      setBusiness(prev => {
        const merged = mergeFields(prev, d.profile, String);
        return { ...merged, website_url: stripWebsiteProtocol(merged.website_url) };
      });
    }
    if (d.addresses?.length > 0) {
      setAddresses(d.addresses);
    }
    if (d.bank_accounts?.length > 0) {
      setBanking(prev => mergeFields(prev, d.bank_accounts[0]));
    }
    applyDocumentsAndAgreements(d);
  };

  const applyDocumentsAndAgreements = (d) => {
    if (d.documents?.length > 0) {
      setDocuments(d.documents.filter(doc => !isAgreementCopyDocument(doc.document_type)));
      const agreementCopy = d.documents.find(doc => isAgreementCopyDocument(doc.document_type));
      if (agreementCopy) {
        setAgreementCopyDoc(agreementCopy);
      }
    }
    if (d.agreement) setAgreementAccepted(true);
    if (d.code_of_conduct) setConductAccepted(true);
    if (d.dsa_empanelment) setDsaDigitalAccepted(true);
    const targetStep = computeTargetStep(d);
    if (targetStep !== null) setCurrentStep(targetStep);
  };

  const computeTargetStep = (d) => {
    const stageMap = {
      basic_info: 0,
      business_details: 1,
      address: 2,
      banking: 3,
      documents: 4,
      agreement: 5,
      conduct: 6,
      dsa_empanelment: 7,
      review: REVIEW_STEP_INDEX,
      completed: REVIEW_STEP_INDEX,
    };
    const stage = d.partner?.onboarding_stage;
    if (!stage || stageMap[stage] === undefined) return null;
    let targetStep = Math.min(stageMap[stage] + 1, REVIEW_STEP_INDEX);
    if (targetStep > 4) {
      targetStep = validateDocStageAdvance(d, targetStep);
    }
    return targetStep;
  };

  const validateDocStageAdvance = (d, targetStep) => {
    const requiredDocs = kycDocumentTypes.filter(dt => dt.required);
    const currentDocs = d.documents?.filter(doc => !isAgreementCopyDocument(doc.document_type)) || [];
    const missingDocs = requiredDocs.filter(dt => !currentDocs.find(doc => doc.document_type === dt.value));
    if (missingDocs.length > 0) {
      toast.error(`Please upload required documents: ${missingDocs.map(d => d.label).join(', ')}`);
      return 4;
    }
    return targetStep;
  };

  useEffect(() => {
    if (partnerId) {
      setLoading(true);
      api.get(`/api/partners/${partnerId}`).then(res => loadPartnerData(res.data)).catch(() => {}).finally(() => setLoading(false));
    }
  }, [partnerId]);

  useEffect(() => {
    setEmailVerified(!!user?.is_email_verified);
    setMobileVerified(!!user?.is_mobile_verified);
    if (user?.email) setBasic(p => (p.email === user.email ? p : { ...p, email: user.email }));
    if (user?.mobile != null && user.mobile !== '') {
      setBasic(p => (p.mobile === user.mobile ? p : { ...p, mobile: user.mobile || '' }));
    }
  }, [user?.is_email_verified, user?.is_mobile_verified, user?.email, user?.mobile]);

  const getVerificationGateMessage = () => {
    if (!emailVerified) {
      return 'Please verify your email address before moving to the next step.';
    }
    if (basic.mobile && basic.mobile.length > 0 && !mobileVerified) {
      return 'You added a mobile number — please verify it before moving to the next step.';
    }
    return '';
  };

  const validateAndSaveBasicInfo = async () => {
    if (!basic.first_name.trim()) { toast.error('First name is required'); return false; }
    if (!/^[a-zA-Z\s]+$/.test(basic.first_name)) { toast.error('First name must contain only alphabetic characters'); return false; }
    if (basic.last_name && !/^[a-zA-Z\s]+$/.test(basic.last_name)) { toast.error('Last name must contain only alphabetic characters'); return false; }
    if (basic.mobile && (!/^\d+$/.test(basic.mobile) || basic.mobile.length !== 10)) { toast.error('Mobile number must be exactly 10 digits'); return false; }
    if (!basic.category) { toast.error('Please select a partner category'); return false; }
    if (basic.partner_type === 'individual' && basic.category === 'corporate') {
      toast.error('Individual partner type cannot use Corporate category');
      return false;
    }
    if (basic.category === 'other' && !basic.category_other?.trim()) {
      toast.error('Please describe what Other category means');
      return false;
    }
    if (!basic.display_name) { toast.error('Please enter display name'); return false; }

    const gate = getVerificationGateMessage();
    if (gate) {
      setVerificationGateMessage(gate);
      toast.error(gate);
      return false;
    }
    setVerificationGateMessage('');

    const res = await api.post('/api/partners/register/basic', {
      ...basic,
      category_other: basic.category === 'other' ? basic.category_other.trim() : null,
    });
    setPartnerId(res.data.partner_id);
    await refreshUser();
    toast.success('Basic info saved!');
    return true;
  };

  const validateAndSaveBusiness = async () => {
    if (!validateAllBusiness()) {
      const fieldLabels = {
        business_name: 'Business Name',
        pan_number: 'PAN Number',
        gst_number: 'GST Number',
        years_in_business: 'Years in Business',
        designation: 'Designation',
        business_description: 'Business Description',
        website_url: 'Website URL'
      };
      const fieldValues = {
        business_name: business.business_name,
        pan_number: business.pan_number,
        gst_number: business.gst_number,
        years_in_business: business.years_in_business,
        designation: business.designation,
        business_description: business.business_description,
        website_url: business.website_url
      };
      const freshErrors = {};
      freshErrors.business_name = validateBusinessName(business.business_name);
      freshErrors.pan_number = validatePanNumber(business.pan_number);
      freshErrors.gst_number = validateGstNumber(business.gst_number);
      freshErrors.years_in_business = validateYearsInBusiness(business.years_in_business);
      freshErrors.designation = validateDesignation(business.designation);
      freshErrors.business_description = validateBusinessDescription(business.business_description);
      freshErrors.website_url = validateWebsite(business.website_url);
      const missingFields = Object.entries(freshErrors)
        .filter(([k, v]) => v && (!fieldValues[k] || !String(fieldValues[k]).trim()))
        .map(([k]) => fieldLabels[k] || k);
      const invalidFields = Object.entries(freshErrors)
        .filter(([k, v]) => v && fieldValues[k] && String(fieldValues[k]).trim())
        .map(([k]) => fieldLabels[k] || k);
      let message = '';
      if (missingFields.length > 0) {
        message += `Please fill the mandatory fields: ${missingFields.join(', ')}`;
      }
      if (invalidFields.length > 0) {
        if (message) message += '. ';
        message += `Please enter valid input for: ${invalidFields.join(', ')}`;
      }
      toast.error(message);
      return false;
    }
    await api.put(`/api/partners/${partnerId}/business`, {
      ...business,
      business_name: business.business_name?.trim() || null,
      business_description: business.business_description?.trim() || null,
      designation: business.designation?.trim() || null,
      website_url: business.website_url?.trim() ? withHttps(business.website_url) : null,
      years_in_business: business.years_in_business ? parseInt(business.years_in_business) : null
    });
    toast.success('Business details saved!');
    return true;
  };

  const validateAndSaveAddress = async () => {
    const validAddresses = addresses.filter(a => a.address_line1 && a.city && a.state && a.pincode);
    if (validAddresses.length === 0) { toast.error('Please add at least one address'); return false; }
    const invalidLine1 = validAddresses.find(a => !/^[a-zA-Z0-9][a-zA-Z0-9\s\-/&,'#.()":;_]*$/.test(a.address_line1.trim()));
    if (invalidLine1) { toast.error('Address Line 1 must start with a letter or number and contain only allowed characters'); return false; }
    const invalidLine2 = validAddresses.find(a => a.address_line2 && !/^[a-zA-Z0-9][a-zA-Z0-9\s\-/&,'#.()":;_]*$/.test(a.address_line2.trim()));
    if (invalidLine2) { toast.error('Address Line 2 must start with a letter or number and contain only allowed characters'); return false; }
    const invalidPincode = validAddresses.find(a => !/^\d{6}$/.test(a.pincode));
    if (invalidPincode) { toast.error('Pincode must be exactly 6 digits'); return false; }
    if (validAddresses.find(a => !a.state || !a.city)) {
      toast.error('Please select State and City from the list');
      return false;
    }
    const payload = validAddresses.map(({ address_type, address_line1, address_line2, city, state, pincode, country }) => ({
      address_type,
      address_line1,
      address_line2: address_line2 || null,
      city,
      state,
      pincode,
      country: country || 'India',
    }));
    await api.put(`/api/partners/${partnerId}/addresses`, payload);
    toast.success('Addresses saved!');
    return true;
  };

  const validateAndSaveBanking = async () => {
    if (!validateAllBanking()) {
      const fieldLabels = {
        upi_id: 'UPI ID',
        linked_mobile: 'Linked Mobile Number',
        account_holder_name: 'Account Holder Name',
        bank_name: 'Bank Name',
        account_number: 'Account Number',
        ifsc_code: 'IFSC Code',
      };
      const errors = {
        upi_id: validateUpiId(banking.upi_id),
        linked_mobile: validateLinkedMobile(banking.linked_mobile),
        account_holder_name: validateAccountHolderName(banking.account_holder_name),
        bank_name: validateBankName(banking.bank_name),
        account_number: validateAccountNumber(banking.account_number),
        ifsc_code: validateIfscCode(banking.ifsc_code),
      };
      const invalidFields = Object.entries(errors)
        .filter(([, v]) => v)
        .map(([k]) => fieldLabels[k] || k);
      toast.error(`Please enter valid payment details: ${invalidFields.join(', ')}`);
      return false;
    }
    await api.put(`/api/partners/${partnerId}/banking`, {
      upi_id: normalizeUpiIdInput(banking.upi_id),
      linked_mobile: normalizeLinkedMobileInput(banking.linked_mobile),
      account_holder_name: banking.account_holder_name.trim(),
      bank_name: banking.bank_name.trim(),
      branch_name: banking.branch_name.trim(),
      account_number: banking.account_number.trim(),
      ifsc_code: banking.ifsc_code.trim().toUpperCase(),
      account_type: banking.account_type || 'savings',
    });
    toast.success('Payment details saved!');
    return true;
  };

  const extractErrorMessage = (err, fallback) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
    return fallback;
  };

  const validateDocumentsStep = () => {
    const requiredDocs = kycDocumentTypes.filter(dt => dt.required);
    const missingDocs = requiredDocs.filter(dt => !documents.find(d => d.document_type === dt.value));
    if (missingDocs.length > 0) {
      toast.error(`Please upload required documents: ${missingDocs.map(d => d.label).join(', ')}`);
      return false;
    }
    toast.success('Documents saved!');
    return true;
  };

  const saveStep = async () => {
    setSaving(true);
    try {
      let success = false;
      switch (currentStep) {
        case 0: success = await validateAndSaveBasicInfo(); break;
        case 1: success = await validateAndSaveBusiness(); break;
        case 2: success = await validateAndSaveAddress(); break;
        case 3: success = await validateAndSaveBanking(); break;
        case 4: success = validateDocumentsStep(); break;
        case 5: success = await validateAndSaveAgreement(); break;
        case 6: success = await validateAndSaveConduct(); break;
        case 7: success = await validateAndSaveDsaEmpanelment(); break;
        default: success = true;
      }
      setSaving(false);
      return success;
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to save'));
      setSaving(false);
      return false;
    }
  };

  // Scroll to top of the main container whenever the step changes
  useEffect(() => {
    const main = document.querySelector('main');
    if (main) main.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const canProceedFromDocuments = () => {
    const requiredDocs = kycDocumentTypes.filter(dt => dt.required);
    const missingDocs = requiredDocs.filter(dt => !documents.find(d => d.document_type === dt.value));
    if (missingDocs.length > 0) {
      toast.error(`Cannot proceed. Please upload: ${missingDocs.map(d => d.label).join(', ')}`);
      return false;
    }
    return true;
  };

  const handleNext = async () => {
    const success = await saveStep();
    if (!success) return;
    if (currentStep === 4 && !canProceedFromDocuments()) return;
    setCurrentStep(prev => Math.min(prev + 1, LAST_STEP_INDEX));
  };

  const handleBack = () => setCurrentStep(prev => Math.max(prev - 1, 0));

  const handleSubmit = async () => {
    if (!dsaDigitalAccepted) {
      const message = 'Please agree to the Agreement before submitting your application.';
      setAgreementCopyError(message);
      toast.error(message);
      return;
    }
    setAgreementCopyError('');
    setSubmitting(true);
    try {
      await api.post(`/api/partners/${partnerId}/submit`);
      toast.success('Application submitted successfully!');
      await refreshUser();
      // Small delay to ensure auth context propagates before navigation
      await new Promise(resolve => setTimeout(resolve, 150));
      navigate('/partner', { replace: true, state: { fromRegistration: true } });
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to submit'));
    }
    setSubmitting(false);
  };

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  const uploadDocument = async (docType, file) => {
    if (!partnerId) return;

    const typeError = validateDocumentFileExtension(docType, file.name);
    if (typeError) {
      toast.error(typeError);
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('File size exceeds 5MB limit');
      return;
    }

    setUploadingDocType(docType);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', docType);
      const res = await api.post(`/api/partners/${partnerId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setDocuments(prev => [...prev.filter(d => d.document_type !== docType), res.data.document]);
      toast.success('Document uploaded!');
    } catch (err) {
      // Check if it's a file size error (413 status)
      if (err.response?.status === 413) {
        toast.error('File is too large. Maximum size allowed is 5MB');
      } else {
        toast.error(extractErrorMessage(err, 'Upload failed'));
      }
    }
    setUploadingDocType(null);
  };

  const validateAndSaveAgreement = async () => {
    if (!agreementAccepted) {
      toast.error('Please accept the Partner Agreement');
      return false;
    }
    const payload = { user_agent: navigator.userAgent, session_id: localStorage.getItem('token') || '' };
    await api.post(`/api/partners/${partnerId}/agreement/accept`, { ...payload, agreement_type: 'nda' });
    toast.success('Agreement accepted!');
    return true;
  };

  const validateAndSaveConduct = async () => {
    if (!conductAccepted) {
      toast.error('Please accept the Code of Conduct');
      return false;
    }
    const payload = { user_agent: navigator.userAgent, session_id: localStorage.getItem('token') || '' };
    await api.post(`/api/partners/${partnerId}/agreement/accept`, { ...payload, agreement_type: 'aml_policy' });
    toast.success('Code of Conduct accepted!');
    return true;
  };

  const validateAndSaveDsaEmpanelment = async () => {
    if (!dsaDigitalAccepted) {
      const message = 'Please agree to the above Agreement to continue.';
      setAgreementCopyError(message);
      toast.error(message);
      return false;
    }
    if (!agreementCopyDoc) {
      toast.error('Agreement PDF is still generating. Please wait and try again.');
      return false;
    }
    setAgreementCopyError('');
    const payload = { user_agent: navigator.userAgent, session_id: localStorage.getItem('token') || '' };
    const res = await api.post(`/api/partners/${partnerId}/agreement/accept`, {
      ...payload,
      agreement_type: DSA_EMPANELMENT_AGREEMENT_TYPE,
    });
    if (res.data?.document) setAgreementCopyDoc(res.data.document);
    toast.success('Agreement accepted!');
    return true;
  };

  const renderStepContent = () => { // NOSONAR - wizard step rendering requires branching per step
    switch (currentStep) {
      case 0: return (
        <div className="space-y-4">
          <div>
            <h2 className="font-display text-base font-semibold text-slate-900">Basic Information</h2>
            <p className="text-xs text-slate-500 mt-0.5">Tell us about yourself to get started</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="basic-partner-type" className="text-sm font-medium text-slate-700 block mb-1.5">Partner Type *</label>
              <Select
                value={basic.partner_type}
                onValueChange={v => setBasic(p => ({
                  ...p,
                  partner_type: v,
                  category: (v === 'individual' && p.category === 'corporate') ? '' : p.category,
                  category_other: (v === 'individual' && p.category === 'corporate') ? '' : p.category_other,
                }))}
              >
                <SelectTrigger id="basic-partner-type" className="rounded-xl border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent>{partnerTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label htmlFor="basic-category" className="text-sm font-medium text-slate-700 block mb-1.5">Category *</label>
              <Select
                value={basic.category}
                onValueChange={v => setBasic(p => ({
                  ...p,
                  category: v,
                  category_other: v === 'other' ? p.category_other : '',
                }))}
              >
                <SelectTrigger id="basic-category" className="rounded-xl border-slate-200" data-testid="category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categoriesForPartnerType(basic.partner_type).map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {basic.category === 'other' && (
              <div className="sm:col-span-2">
                <label htmlFor="basic-category-other" className="text-sm font-medium text-slate-700 block mb-1.5">Describe Other Category *</label>
                <Input
                  id="basic-category-other"
                  value={basic.category_other}
                  onChange={e => setBasic(p => ({ ...p, category_other: e.target.value.slice(0, 120) }))}
                  placeholder="e.g. Mutual fund distributor, Fintech aggregator"
                  maxLength={120}
                  className="rounded-xl border-slate-200"
                  data-testid="category-other-input"
                />
                <p className="text-[11px] text-slate-400 mt-1">{(basic.category_other || '').length}/120</p>
              </div>
            )}
            <div>
              <label htmlFor="basic-first-name" className="text-sm font-medium text-slate-700 block mb-1.5">First Name *</label>
              <Input id="basic-first-name" value={basic.first_name} onChange={e => { const v = e.target.value.replace(/[^a-zA-Z\s]/g, ''); setBasic(p => ({ ...p, first_name: v, display_name: `${v} ${p.last_name}`.trim() })); }} className="rounded-xl border-slate-200" pattern="[a-zA-Z\s]+" title="Only alphabetic characters allowed" data-testid="basic-firstname" />
            </div>
            <div>
              <label htmlFor="basic-last-name" className="text-sm font-medium text-slate-700 block mb-1.5">Last Name</label>
              <Input id="basic-last-name" value={basic.last_name} onChange={e => { const v = e.target.value.replace(/[^a-zA-Z\s]/g, ''); setBasic(p => ({ ...p, last_name: v, display_name: `${p.first_name} ${v}`.trim() })); }} className="rounded-xl border-slate-200" pattern="[a-zA-Z\s]+" title="Only alphabetic characters allowed" data-testid="basic-lastname" />
            </div>
            <div className="sm:col-span-2">
              <ContactVerificationControls
                mode="onboarding"
                email={basic.email}
                mobile={basic.mobile}
                emailVerified={emailVerified}
                mobileVerified={mobileVerified}
                showGateMessage={Boolean(verificationGateMessage)}
                gateMessage={verificationGateMessage}
                onMobileChange={(v) => {
                  setBasic(p => ({ ...p, mobile: v }));
                  // Changing mobile after verify requires re-verification
                  if (mobileVerified && v !== (user?.mobile || '')) {
                    setMobileVerified(false);
                    updateUser({ is_mobile_verified: false, mobile: v });
                  }
                  if (verificationGateMessage) setVerificationGateMessage('');
                }}
                onVerificationChange={async (data) => {
                  setEmailVerified(!!data.is_email_verified);
                  setMobileVerified(!!data.is_mobile_verified);
                  updateUser({
                    is_email_verified: !!data.is_email_verified,
                    is_mobile_verified: !!data.is_mobile_verified,
                    ...(data.mobile != null ? { mobile: data.mobile } : {}),
                  });
                  if (data.mobile) setBasic(p => ({ ...p, mobile: data.mobile }));
                  setVerificationGateMessage('');
                  await refreshUser();
                }}
              />
            </div>
          </div>
        </div>
      );
      case 1: return (
        <div className="space-y-4">
          <div>
            <h2 className="font-display text-base font-semibold text-slate-900">Business Details</h2>
            <p className="text-xs text-slate-500 mt-0.5">Provide your business information and compliance details</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="business-constitution-type" className="text-sm font-medium text-slate-700 block mb-1.5">Constitution Type</label>
              <Select value={business.constitution_type} onValueChange={v => setBusiness(p => ({ ...p, constitution_type: v }))}>
                <SelectTrigger id="business-constitution-type" className="rounded-xl border-slate-200"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {CONSTITUTION_TYPES.map(t => (
                    <SelectItem key={t} value={t}>{constitutionTypeLabels[t] || t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label htmlFor="business-name" className="text-sm font-medium text-slate-700 block mb-1.5">Business Name *</label>
              <Input id="business-name" value={business.business_name} onChange={handleBusinessNameChange} maxLength={100} className={`rounded-xl border-slate-200 ${businessErrors.business_name ? 'border-red-400 focus-visible:ring-red-400' : ''}`} />
              {businessErrors.business_name && <p className="text-xs text-red-500 mt-1">{businessErrors.business_name}</p>}
            </div>
            <div>
              <label htmlFor="business-pan" className="text-sm font-medium text-slate-700 block mb-1.5">PAN Number *</label>
              <Input id="business-pan" value={business.pan_number} onChange={handlePANChange} placeholder="ABCDE1234F" maxLength={10} className={`rounded-xl border-slate-200 ${businessErrors.pan_number ? 'border-red-400 focus-visible:ring-red-400' : ''}`} />
              {businessErrors.pan_number && <p className="text-xs text-red-500 mt-1">{businessErrors.pan_number}</p>}
            </div>
            <div>
              <label htmlFor="business-gst" className="text-sm font-medium text-slate-700 block mb-1.5">GST Number <span className="text-slate-400 font-normal"></span></label>
              <Input id="business-gst" value={business.gst_number} onChange={handleGSTChange} placeholder="22ABCDE1234F1Z5" maxLength={15} className={`rounded-xl border-slate-200 ${businessErrors.gst_number ? 'border-red-400 focus-visible:ring-red-400' : ''}`} />
              {businessErrors.gst_number && <p className="text-xs text-red-500 mt-1">{businessErrors.gst_number}</p>}
            </div>
            <div>
              <label htmlFor="business-years" className="text-sm font-medium text-slate-700 block mb-1.5">Years in Business *</label>
              <Input id="business-years" type="text" inputMode="numeric" value={business.years_in_business} onChange={handleYearsChange} placeholder="e.g. 5" className={`rounded-xl border-slate-200 ${businessErrors.years_in_business ? 'border-red-400 focus-visible:ring-red-400' : ''}`} />
              {businessErrors.years_in_business && <p className="text-xs text-red-500 mt-1">{businessErrors.years_in_business}</p>}
            </div>
            <div>
              <label htmlFor="business-designation" className="text-sm font-medium text-slate-700 block mb-1.5">Designation *</label>
              <Input id="business-designation" value={business.designation} onChange={handleDesignationChange} maxLength={100} placeholder="e.g. Director, Partner, Proprietor" className={`rounded-xl border-slate-200 ${businessErrors.designation ? 'border-red-400 focus-visible:ring-red-400' : ''}`} />
              {businessErrors.designation && <p className="text-xs text-red-500 mt-1">{businessErrors.designation}</p>}
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="business-description" className="text-sm font-medium text-slate-700 block mb-1.5">Business Description *</label>
              <Textarea id="business-description" value={business.business_description} onChange={handleDescriptionChange} rows={3} maxLength={500} className={`rounded-xl border-slate-200 ${businessErrors.business_description ? 'border-red-400 focus-visible:ring-red-400' : ''}`} />
              <div className="flex items-center justify-between mt-1">
                {businessErrors.business_description && <p className="text-xs text-red-500">{businessErrors.business_description}</p>}
              </div>
            </div>
            <div>
              <label htmlFor="business-website" className="text-sm font-medium text-slate-700 block mb-1.5">Website</label>
              <div className={`flex items-center rounded-xl border bg-white overflow-hidden ${businessErrors.website_url ? 'border-red-400' : 'border-slate-200'}`}>
                <span className="px-3 text-sm text-slate-500 bg-slate-50 border-r border-slate-200 select-none shrink-0">https://</span>
                <Input
                  id="business-website"
                  value={stripWebsiteProtocol(business.website_url)}
                  onChange={handleWebsiteChange}
                  placeholder="www.example.com"
                  className="rounded-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
              {businessErrors.website_url && <p className="text-xs text-red-500 mt-1">{businessErrors.website_url}</p>}
            </div>
          </div>
        </div>
      );
      case 2: return <AddressStep addresses={addresses} setAddresses={setAddresses} />;
      case 3: return (
        <div className="space-y-8">
          <div>
            <h2 className="font-display text-lg font-semibold text-slate-900">Banking / Payment Details</h2>
            <p className="text-sm text-slate-500 mt-1">Enter your UPI and bank account details for commission payouts</p>
          </div>

          {/* UPI Details Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-800 border-b border-slate-100 pb-2">UPI Details</h3>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="banking-upi" className="text-sm font-medium text-slate-700 block mb-1.5">UPI ID *</label>
                <Input
                  id="banking-upi"
                  value={banking.upi_id}
                  onChange={e => {
                    const v = sanitizeUpiIdKeystroke(e.target.value);
                    setBanking(p => ({ ...p, upi_id: v }));
                    if (bankingErrors.upi_id) setBankingErrors(prev => ({ ...prev, upi_id: validateUpiId(v) }));
                  }}
                  onBlur={() => setBankingErrors(prev => ({ ...prev, upi_id: validateUpiId(banking.upi_id) }))}
                  placeholder="name@upi"
                  className={`rounded-xl border-slate-200 ${bankingErrors.upi_id ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                  data-testid="banking-upi"
                />
                {bankingErrors.upi_id && <p className="text-xs text-red-500 mt-1" data-testid="banking-upi-error">{bankingErrors.upi_id}</p>}
              </div>
              <div>
                <label htmlFor="banking-mobile" className="text-sm font-medium text-slate-700 block mb-1.5">Linked Mobile Number *</label>
                <Input
                  id="banking-mobile"
                  value={banking.linked_mobile}
                  onChange={e => {
                    // Digits only for typing UX; do not silently truncate length
                    const v = sanitizeLinkedMobileKeystroke(e.target.value);
                    setBanking(p => ({ ...p, linked_mobile: v }));
                    if (bankingErrors.linked_mobile) {
                      setBankingErrors(prev => ({ ...prev, linked_mobile: validateLinkedMobile(v) }));
                    }
                  }}
                  onBlur={() => setBankingErrors(prev => ({
                    ...prev,
                    linked_mobile: validateLinkedMobile(banking.linked_mobile),
                  }))}
                  inputMode="numeric"
                  placeholder="10-digit mobile number"
                  className={`rounded-xl border-slate-200 ${bankingErrors.linked_mobile ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                  data-testid="banking-mobile"
                />
                {bankingErrors.linked_mobile && (
                  <p className="text-xs text-red-500 mt-1" data-testid="banking-mobile-error">{bankingErrors.linked_mobile}</p>
                )}
                {!bankingErrors.linked_mobile && banking.linked_mobile && banking.linked_mobile.length > 0 && banking.linked_mobile.length !== 10 && (
                  <p className="text-[11px] text-[#94a3b8] mt-1">{banking.linked_mobile.length}/10 digits</p>
                )}
              </div>
            </div>
          </div>

          {/* Bank Details Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-800 border-b border-slate-100 pb-2">Bank Details</h3>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="banking-account-holder" className="text-sm font-medium text-slate-700 block mb-1.5">Account Holder Name *</label>
                <Input id="banking-account-holder" value={banking.account_holder_name} onChange={e => { const v = e.target.value.replace(/[^a-zA-Z\s.]/g, '').slice(0, 200); setBanking(p => ({ ...p, account_holder_name: v })); if (bankingErrors.account_holder_name) setBankingErrors(prev => ({ ...prev, account_holder_name: validateAccountHolderName(v) })); }} placeholder="As per bank records" className={`rounded-xl border-slate-200 ${bankingErrors.account_holder_name ? 'border-red-400 focus-visible:ring-red-400' : ''}`} data-testid="banking-account-holder" />
                {bankingErrors.account_holder_name && <p className="text-xs text-red-500 mt-1">{bankingErrors.account_holder_name}</p>}
              </div>
              <div>
                <label htmlFor="banking-bank-name" className="text-sm font-medium text-slate-700 block mb-1.5">Bank Name *</label>
                <Input id="banking-bank-name" value={banking.bank_name} onChange={e => { const v = e.target.value.replace(/[^a-zA-Z0-9\s&.\-()]/g, '').slice(0, 200); setBanking(p => ({ ...p, bank_name: v })); if (bankingErrors.bank_name) setBankingErrors(prev => ({ ...prev, bank_name: validateBankName(v) })); }} placeholder="e.g. HDFC Bank" className={`rounded-xl border-slate-200 ${bankingErrors.bank_name ? 'border-red-400 focus-visible:ring-red-400' : ''}`} data-testid="banking-bank-name" />
                {bankingErrors.bank_name && <p className="text-xs text-red-500 mt-1">{bankingErrors.bank_name}</p>}
              </div>
              <div>
                <label htmlFor="banking-branch-name" className="text-sm font-medium text-slate-700 block mb-1.5">Branch Name</label>
                <Input
                  id="banking-branch-name"
                  value={banking.branch_name}
                  onChange={e => {
                    const v = e.target.value.slice(0, 200);
                    setBanking(p => ({ ...p, branch_name: v }));
                    if (bankingErrors.branch_name) setBankingErrors(prev => ({ ...prev, branch_name: validateBranchName(v) }));
                  }}
                  placeholder="e.g. Indore Main Branch"
                  className={`rounded-xl border-slate-200 ${bankingErrors.branch_name ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                  data-testid="banking-branch-name"
                />
                {bankingErrors.branch_name && <p className="text-xs text-red-500 mt-1">{bankingErrors.branch_name}</p>}
              </div>
              <div>
                <label htmlFor="banking-account-number" className="text-sm font-medium text-slate-700 block mb-1.5">Account Number *</label>
                <Input id="banking-account-number" value={banking.account_number} onChange={e => { const v = sanitizeAccountNumberInput(e.target.value); setBanking(p => ({ ...p, account_number: v })); if (bankingErrors.account_number) setBankingErrors(prev => ({ ...prev, account_number: validateAccountNumber(v) })); }} inputMode="numeric" placeholder="9-18 digit account number" className={`rounded-xl border-slate-200 ${bankingErrors.account_number ? 'border-red-400 focus-visible:ring-red-400' : ''}`} data-testid="banking-account-number" />
                {bankingErrors.account_number && <p className="text-xs text-red-500 mt-1">{bankingErrors.account_number}</p>}
              </div>
              <div>
                <label htmlFor="banking-ifsc" className="text-sm font-medium text-slate-700 block mb-1.5">IFSC Code *</label>
                <Input id="banking-ifsc" value={banking.ifsc_code} onChange={e => { const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11); setBanking(p => ({ ...p, ifsc_code: v })); if (bankingErrors.ifsc_code) setBankingErrors(prev => ({ ...prev, ifsc_code: validateIfscCode(v) })); }} placeholder="e.g. HDFC0001234" maxLength={11} className={`rounded-xl border-slate-200 ${bankingErrors.ifsc_code ? 'border-red-400 focus-visible:ring-red-400' : ''}`} data-testid="banking-ifsc" />
                {bankingErrors.ifsc_code && <p className="text-xs text-red-500 mt-1">{bankingErrors.ifsc_code}</p>}
              </div>
            </div>
          </div>
        </div>
      );
      case 4: return (
        <KycDocumentUploadStep
          documents={documents}
          uploadDocument={uploadDocument}
          uploadingDocType={uploadingDocType}
        />
      );
      case 5: return <AgreementStep agreementAccepted={agreementAccepted} setAgreementAccepted={setAgreementAccepted} />;
      case 6: return <ConductStep conductAccepted={conductAccepted} setConductAccepted={setConductAccepted} />;
      case 7: return (
        <DsaEmpanelmentStep
          partnerId={partnerId}
          dsaDigitalAccepted={dsaDigitalAccepted}
          setDsaDigitalAccepted={setDsaDigitalAccepted}
          agreementCopyDoc={agreementCopyDoc}
          setAgreementCopyDoc={setAgreementCopyDoc}
        />
      );
      case 8: return (
        <div className="space-y-6">
          <div>
            <h2 className="font-display text-lg font-semibold text-slate-900">Review & Submit</h2>
            <p className="text-sm text-slate-500 mt-1">Verify your details before submitting your application</p>
          </div>
          <div className="space-y-4">
            <ReviewSection title="Basic Info">
              <ReviewRow label="Name" value={`${basic.first_name} ${basic.last_name}`} />
              <ReviewRow
                label="Category"
                value={
                  basic.category === 'other' && basic.category_other
                    ? `Other (${basic.category_other})`
                    : partnerCategories.find(c => c.value === basic.category)?.label
                }
              />
              <ReviewRow label="Type" value={partnerTypeLabels[basic.partner_type] || basic.partner_type} />
              <ReviewRow label="Email" value={basic.email} />
              <ReviewRow label="Mobile" value={basic.mobile} />
            </ReviewSection>
            <ReviewSection title="Business Details">
              <ReviewRow label="Business" value={business.business_name} />
              <ReviewRow label="PAN" value={business.pan_number} />
              <ReviewRow label="GST" value={business.gst_number} />
            </ReviewSection>
            <ReviewSection title="Address">
              {addresses.map((a) => (
                <ReviewRow key={a._id} label={addressTypeLabels[a.address_type] || a.address_type} value={`${a.address_line1}, ${a.city}, ${a.state} - ${a.pincode}`} />
              ))}
            </ReviewSection>
            <ReviewSection title="Payment">
              <ReviewRow label="UPI ID" value={banking.upi_id} />
              <ReviewRow label="Linked Mobile" value={banking.linked_mobile} />
              <ReviewRow label="Account Holder" value={banking.account_holder_name} />
              <ReviewRow label="Bank Name" value={banking.bank_name} />
              <ReviewRow label="Branch Name" value={banking.branch_name || '—'} />
              <ReviewRow label="Account Number" value={maskAccountNumber(banking.account_number)} />
              <ReviewRow label="IFSC Code" value={banking.ifsc_code} />
            </ReviewSection>
            <ReviewSection title="Documents">
              {documents.length > 0 ? (
                <div className="space-y-1.5">
                  {documents.map((doc) => (
                    <div key={doc.document_type || doc.id} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#10b981]" />
                      <span className="text-sm text-slate-700">{getDocumentTypeLabel(doc.document_type)}</span>
                      {doc.document_name && <span className="text-xs text-slate-400 truncate max-w-[200px]">({doc.document_name})</span>}
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-slate-400 italic">No documents uploaded</p>}
            </ReviewSection>
            <ReviewSection title="Agreements">
              <ReviewRow label="NDA" value={agreementAccepted ? 'Accepted' : 'Not accepted'} />
              <ReviewRow label="AML Policy" value={conductAccepted ? 'Accepted' : 'Not accepted'} />
              <ReviewRow
                label="DSA Empanelment"
                value={
                  dsaDigitalAccepted
                    ? `✓ Agreement Accepted (v${DSA_EMPANELMENT_AGREEMENT_VERSION})${agreementCopyDoc?.document_name ? ` — ${agreementCopyDoc.document_name}` : ''}`
                    : 'Not completed'
                }
              />
            </ReviewSection>
          </div>
          <div className="bg-amber-50/80 border border-amber-200/80 rounded-2xl p-5">
            <div className="flex items-start gap-3">
              <Checkbox
                id="wizard-terms"
                checked={termsAccepted}
                onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                data-testid="wizard-terms-checkbox"
                className="mt-0.5"
              />
              <label htmlFor="wizard-terms" className="text-sm text-amber-800 font-medium cursor-pointer leading-relaxed">
                By submitting, I confirm that all information provided is accurate and I agree to DhanSthal&apos;s Terms and Conditions.
              </label>
            </div>
          </div>
        </div>
      );
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="registration-wizard-loading">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-[#0066CC]" />
          </div>
          <p className="text-sm font-medium text-slate-600">Loading your registration data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in" data-testid="registration-wizard">
      {/* Page Header */}
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Partner Registration</h1>
        <p className="text-sm text-slate-500 mt-1.5">Complete all steps to submit your application</p>
      </div>

      {/* Stepper */}
      <WizardStepper currentStep={currentStep} />

      {/* Step Content */}
      <Card className="rounded-2xl border border-slate-200/80 bg-white shadow-[0_10px_30px_rgba(2,8,23,0.08)]">
        <CardContent className="p-5 sm:p-6">
          {renderStepContent()}
        </CardContent>
      </Card>

      {/* Navigation */}
      <WizardNavigation
        currentStep={currentStep}
        saving={saving}
        submitting={submitting}
        termsAccepted={termsAccepted}
        agreementAccepted={agreementAccepted}
        conductAccepted={conductAccepted}
        dsaStepSatisfied={dsaDigitalAccepted && !!agreementCopyDoc}
        onBack={handleBack}
        onNext={handleNext}
        onSubmit={handleSubmit}
      />

      {/* Agreement Preview Modal */}
      {previewAgreementMode && (
        <AgreementPreviewModal mode={previewAgreementMode} onClose={() => setPreviewAgreementMode(null)} />
      )}
    </div>
  );
}

function ReviewSection({ title, children }) {
  return (
    <div className="bg-slate-50/80 rounded-2xl p-5 border border-slate-100">
      <h3 className="font-display font-semibold text-sm text-slate-700 mb-3">{title}</h3>
      <div className="grid sm:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

ReviewSection.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function ReviewRow({ label, value }) {
  return (
    <div>
      <p className="text-xs text-slate-400">{label}</p>
      <p className="text-sm text-slate-900">{value || '-'}</p>
    </div>
  );
}

ReviewRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
